/**
 * LA CAJA NEGRA DEL ARRANQUE DESDE UN AVISO.
 *
 * POR QUÉ EXISTE (02-09-2026). Tres rondas de arreglos y tres veces el mismo
 * final: con la app abierta el aviso lleva a su sitio, con la app cerrada no.
 * Cada ronda arregló algo real —la consulta que se caía en silencio, el destino
 * que venía hecho y venía mal, el buzón que no estaba puesto a tiempo— y
 * ninguna arregló ESTO, porque cada diagnóstico salía de deducir, no de mirar.
 *
 * Lo que aquí se apunta es lo único que separa las hipótesis que quedan, y no se
 * puede ver desde fuera del teléfono:
 *
 *   · CON QUÉ URL ARRANCA. Si es `/planner?taskId=…`, el envoltorio sí navegó y
 *     el id se pierde ya dentro de la aplicación. Si es `/planner` a secas, es
 *     que el WebView restauró la pantalla de la sesión anterior —el usuario
 *     lleva todo el día probando ahí— y el envoltorio no navegó. Si es otra, ni
 *     una cosa ni la otra.
 *   · SI `onNotificationEvent` LLEGA A LLAMARSE, y cuántos milisegundos después
 *     de empezar a leer el HTML.
 *   · QUÉ CLAVES trae el aviso y qué destino sale de ellas.
 *
 * SE MANDA COMO «info», que inserta la fila pero NO manda correo (ver la puerta
 * de severidad en `log-error`). Y solo desde el contenedor nativo, que es donde
 * ocurre el problema: en el navegador no se apunta nada.
 *
 * NO SE GUARDA NADA PERSONAL: ni títulos, ni nombres, ni valores del aviso. Solo
 * la ruta, los nombres de las claves y si el id venía o no.
 *
 * ES TEMPORAL. En cuanto el arranque en frío funcione, esto se quita.
 */
import { isDespiaNative } from "@/lib/isDespiaNative";
import { logError } from "@/lib/errorLogger";

declare global {
  interface Window {
    __ultureArranque?: {
      inicio: number;
      urlInicial: string;
      avisos: Array<{ ms: number; donde: string }>;
      altoAlArrancar?: number;
      altoDePantalla?: number | null;
      sistemaOscuro?: boolean;
    };
  }
}

/** Nombres de las claves, nunca los valores. Un título de tarea no pinta nada aquí. */
function clavesDe(payload: unknown, profundidad = 3): string[] {
  const vistas = new Set<string>();
  const visitar = (v: unknown, quedan: number, prefijo: string) => {
    if (quedan < 0 || !v || typeof v !== "object" || Array.isArray(v)) return;
    for (const [k, valor] of Object.entries(v as Record<string, unknown>)) {
      vistas.add(prefijo + k);
      visitar(valor, quedan - 1, `${prefijo}${k}.`);
    }
  };
  visitar(payload, profundidad, "");
  return [...vistas].slice(0, 60);
}

let yaMandado = false;

export interface ParteDeArranque {
  /** Lo que resolvió `destinoDe`, o null si no resolvió nada. */
  destino: string | null;
  /** El aviso que se recogió del buzón, si lo hubo. */
  aviso: unknown;
  /** La URL en la que está el router cuando se manda esto. */
  urlAhora: string;
}

/**
 * Manda el parte. Una sola vez por arranque, y solo en el contenedor nativo.
 *
 * Nunca lanza: es un diagnóstico, no puede ser la causa de nada.
 */
export function mandarParteDeArranque(parte: ParteDeArranque): void {
  try {
    if (yaMandado) return;
    if (!isDespiaNative()) return;
    yaMandado = true;

    const caja = typeof window !== "undefined" ? window.__ultureArranque : undefined;
    const avisoRecibido = parte.aviso != null;

    void logError("[diagnóstico] arranque desde aviso", "arranque-aviso", {
      error_type: "frontend",
      severity: "info",
      details: {
        /* La medida que lo decide todo. */
        url_inicial: caja?.urlInicial ?? "(sin caja negra: falta el script del index.html)",
        url_ahora: parte.urlAhora,
        /* ¿Llegó a llamarse `onNotificationEvent`, y cuándo? */
        avisos_recibidos: caja?.avisos ?? [],
        ms_hasta_este_parte: caja ? Date.now() - caja.inicio : null,
        /* Qué se recogió y a dónde llevaba. */
        aviso_recibido: avisoRecibido,
        aviso_claves: avisoRecibido ? clavesDe(parte.aviso) : [],
        destino_calculado: parte.destino,
        /* Para saber si el buzón del HTML sobrevivió al despliegue. */
        buzon_html_presente: !!caja,
        /* La franja negra: ¿modo oscuro del sistema, y creció el viewport? */
        sistema_oscuro: caja?.sistemaOscuro ?? null,
        alto_al_arrancar: caja?.altoAlArrancar ?? null,
        alto_ahora: typeof window !== "undefined" ? window.innerHeight : null,
        alto_de_pantalla: caja?.altoDePantalla ?? null,
        hueco_abajo: (() => {
          try {
            const cs = getComputedStyle(document.documentElement);
            return {
              variable: cs.getPropertyValue("--safe-area-bottom").trim() || "(sin definir)",
              safeBottom: cs.getPropertyValue("--safe-bottom").trim() || "(sin definir)",
            };
          } catch { return null; }
        })(),
        user_agent_despia: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 120) : "",
      },
    });
  } catch {
    /* Ni un diagnóstico roto puede tumbar el arranque. */
  }
}

/** Para los tests. */
export function reiniciarParteDeArranque(): void {
  yaMandado = false;
}
